/**
 * The volatile half of [NF-022] (ADR-C-V08-009, Q-V08-009 = hybrid): what a
 * mask holds that the server has not confirmed yet.
 *
 * A module-scoped map and nothing else - never localStorage, sessionStorage or
 * IndexedDB. The answer to Q-V08-009 is explicit that free text must not lie
 * in browser storage, where [NF-004] protects exactly this class of content;
 * the server is the leading copy. What the buffer covers is the gap the server
 * cannot: the connection is down, or the session has just run out, and the
 * mask unmounts. Within the same page lifetime the next mount of that mask -
 * after a re-login without a reload, after an accidental navigation - finds
 * the unconfirmed state here and offers it.
 *
 * Keyed by person, mask and context, so a second person signing in on the
 * same tab never sees the first one's input.
 */

export type DraftPayload = Record<string, string | string[]>;

export interface BufferedDraft {
  payload: DraftPayload;
  /** epoch ms of the last change the mask reported. */
  changedAt: number;
  /** True once the server confirmed exactly this payload. */
  synced: boolean;
}

const entries = new Map<string, BufferedDraft>();

export function draftBufferKey(userId: string, form: string, context: string): string {
  return `${userId}|${form}|${context}`;
}

export function readBuffered(key: string): BufferedDraft | undefined {
  return entries.get(key);
}

/** A change the server has not confirmed yet. */
export function bufferChange(key: string, payload: DraftPayload, now: number): void {
  entries.set(key, { payload, changedAt: now, synced: false });
}

/**
 * The server confirmed `payload`. Only the entry holding that very object is
 * marked: a newer change made while the request was in flight stays unsynced.
 */
export function markSynced(key: string, payload: DraftPayload): void {
  const entry = entries.get(key);
  if (entry && entry.payload === payload) entries.set(key, { ...entry, synced: true });
}

export function dropBuffered(key: string): void {
  entries.delete(key);
}

/** Forget everything - on an explicit logout, where nothing should outlive the person's session. */
export function clearDraftBuffer(): void {
  entries.clear();
}
